import React, { useEffect, useState, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { Box, Typography, IconButton, Fab, useTheme, useMediaQuery } from '@mui/material';
import { ArrowBack, KeyboardArrowDown } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { currentUser } from '../../../src/pages/Messages/mockData';
import { useMessageContext } from '../../hook/useMessageContext';
import type { IMessage, IMessageReaction, IRoom } from '../../redux/messages/types';
import { useSelector } from 'react-redux';
import {
    roomsSelector,
    selectedRoomMessagesSelector,
    fetchingMessagesSelector,
    messagesPaginationSelector,
    loadingMoreMessagesSelector,
    roomParticipantsSelector,
} from '../../selectors/selectors';
import { IUserSummary } from '../../types/global/helper';
import { createFullName, getJWtDetails } from '../../utils/AppUtils';
import { ThspianAvatar } from '../../components/Avatar/ThspianAvatar';
import { useDispatch } from 'react-redux';
import {
    joinChatRoom,
    getMessagesForSelectedRoom,
    sendMessage,
    typingStatusSocket,
    updateMessage,
    deleteMessage,
    reactToMessage,
    queueFileUpload,
} from '../../redux/messages/actions';
import { MessageSkeletonGroup } from '../../components/MessageSkeleton/MessageSkeletonGroup';
import { ScrollableContainer } from '../../components/ScrollableContainer/ScrollableContainer';
import { v4 as uuidv4 } from 'uuid';
import { MessageBubble } from '../../components/MessageBubble';
import {
    MessageInput,
    AttachmentType,
    IMediaAttachment,
    IProjectAttachment,
    ILocationAttachment,
} from '../../../src/pages/Messages/components/MessageInput';
import Compressor from 'compressorjs';
import { showToastMessage } from '../../utils/AppUtils';
import ExpandIcon from '../../components/CustomSVGIcons/ExpandIcon';
import { CloseIcon } from '@chakra-ui/icons';
import {
    toggleAvatarMessageModal,
    toggleIsMessageModalExpanded,
    setMessagesExpandedReturnTarget,
} from '../../redux/appConfigurator/action';
import { isMessageModalExpandedSelector } from '../../redux/appConfigurator/selectors';

// Since we're not using Redux, we need an interface for the OutletContext
interface OutletContextType {
    roomId: string;
}
interface ConversationViewProps {
    roomId?: string | null;
    onClickMessageButton?: () => void;
    onRoomClick?: (roomId: string | undefined) => void;
    /** Avatar chat fills the viewport (small mobile floating shell). */
    embedFullScreen?: boolean;
}

// Interface for custom reaction format
interface ICustomReaction {
    emoji: string;
    users: string[];
}

const smoothScrollTo = (element: HTMLElement, targetScrollTop: number, duration = 150) => {
    const start = element.scrollTop;
    const change = targetScrollTop - start;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        element.scrollTop = start + change * easeInOutQuad(progress);
        if (progress < 1) {
            requestAnimationFrame(animateScroll);
        }
    };

    const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    requestAnimationFrame(animateScroll);
};

const FloatingConversationView: React.FC<ConversationViewProps> = ({
    roomId: propRoomId,
    onRoomClick,
    onClickMessageButton,
    embedFullScreen,
}) => {
    const params = useParams<{ roomId: string }>();
    const outletContext = useOutletContext<OutletContextType | null>();
    // ✅ Prefer prop, then outlet context, then route param
    const roomId = propRoomId || outletContext?.roomId || params?.roomId;
    const { themeStyle } = useThemeContext();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const roomMessages = useSelector(selectedRoomMessagesSelector);
    const participants = useSelector(roomParticipantsSelector);
    const rooms = useSelector(roomsSelector);
    const loadingMore = useSelector(loadingMoreMessagesSelector);
    const fetchingMessages = useSelector(fetchingMessagesSelector);
    const isExpanded = useSelector(isMessageModalExpandedSelector);

    const { user_id, user } = getJWtDetails();
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [room, setRoom] = useState<IRoom | undefined>(rooms.find(r => r._id === roomId));
    const dispatch = useDispatch();
    const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isCurrentlyTyping = useRef(false);
    const uiTheme = useTheme();
    const smallScreens = useMediaQuery(uiTheme.breakpoints.down(750));

    const { setActiveRoomId, message, setMessage } = useMessageContext();

    const isTypingText = useMemo(() => {
        if (!room) return '';

        const { receiver, sender, userTyping } = room;
        const combinedUsers = [...receiver, sender].filter(
            (u): u is IUserSummary => Boolean(u) && typeof u !== 'string',
        );
        const otherParticipantIds = new Set(
            combinedUsers.map(user => String(user._id)).filter(participantId => participantId !== String(user_id)),
        );
        const typingIds = new Set((userTyping ?? []).map(id => String(id)));
        const typingUsers = combinedUsers.filter(
            user => otherParticipantIds.has(String(user._id)) && typingIds.has(String(user._id)),
        );
        const names = typingUsers.map(user => user.firstName).filter(Boolean);

        if (names.length === 0) return '';
        if (names.length === 1) return `${names[0]} is typing...`;
        if (names.length === 2) return `${names[0]} & ${names[1]} are typing...`;
        if (names.length === 3) return `${names[0]}, ${names[1]} & ${names[2]} are typing...`;

        const remaining = names.length - 1;
        return `${names[0]} & ${remaining} others are typing...`;
    }, [room, user_id]);

    // New state for reply and reactions
    const [replyingTo, setReplyingTo] = useState<IMessage | null>(null);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const updatingReaction = useRef(false);

    const messagesPagination = useSelector(messagesPaginationSelector);

    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

    // State to track the scroll position when loading more messages
    const [scrollAnchorPosition, setScrollAnchorPosition] = useState<number | null>(null);
    const [scrollAnchorHeight, setScrollAnchorHeight] = useState<number | null>(null);

    // State for attachments - we only allow one attachment per message
    const [attachments, setAttachments] = useState<AttachmentType[]>([]);

    // File size constraints (in bytes)
    const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB
    const MAX_DOCUMENT_SIZE = 1 * 1024 * 1024; // 1MB
    const COMPRESSION_THRESHOLD = 1 * 1024 * 1024; // 1MB
    const TARGET_COMPRESSED_SIZE = 150 * 1024; // 150KB

    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const shouldStickToBottomRef = useRef(true);
    const prevLatestMessageIdRef = useRef<string | null>(null);
    const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);
    const participantRotationInterval = useRef<ReturnType<typeof setInterval> | null>(null);

    // Get other participants (excluding current user)
    const otherParticipants = useMemo(() => {
        if (!participants || participants.length === 0) return [];
        return participants.filter(p => p.user._id !== user_id);
    }, [participants, user_id]);

    // Get current participant to display (for rotation if more than 2)
    const currentParticipant = useMemo(() => {
        if (otherParticipants.length === 0) return null;
        if (otherParticipants.length <= 2) {
            // If 2 or fewer, show first one
            return otherParticipants[0];
        }
        // If more than 2, rotate through them
        return otherParticipants[currentParticipantIndex % otherParticipants.length];
    }, [otherParticipants, currentParticipantIndex]);

    // Set up rotation interval for multiple participants
    useEffect(() => {
        if (otherParticipants.length > 2) {
            participantRotationInterval.current = setInterval(() => {
                setCurrentParticipantIndex(prev => (prev + 1) % otherParticipants.length);
            }, 3000); // Rotate every 3 seconds

            return () => {
                if (participantRotationInterval.current) {
                    clearInterval(participantRotationInterval.current);
                }
            };
        } else {
            setCurrentParticipantIndex(0);
            if (participantRotationInterval.current) {
                clearInterval(participantRotationInterval.current);
            }
        }
    }, [otherParticipants.length]);

    const handleGetRoomMessages = useCallback(() => {
        if (roomId) {
            dispatch(
                joinChatRoom({
                    roomID: roomId,
                    userID: user_id,
                }),
            );
            setRoom(rooms.find(r => r._id === roomId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId, rooms]);

    // Keep local room in sync with Redux room updates (typing, unread, presence, etc).
    useEffect(() => {
        if (!roomId) return;
        const nextRoom = rooms.find(r => r._id === roomId);
        if (nextRoom) {
            setRoom(nextRoom);
        }
    }, [roomId, rooms]);

    // Listen to Redux state change and update local message list
    useEffect(() => {
        if (containerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
            shouldStickToBottomRef.current = scrollHeight - scrollTop - clientHeight < 120;
        }
        if (roomId) {
            setMessages(roomMessages);
        }
    }, [roomId, roomMessages]);

    // Function to check if scroll container is at the bottom
    const isScrolledToBottom = useCallback(() => {
        if (containerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
            // Consider "at bottom" if within 120px of the bottom
            return scrollHeight - scrollTop - clientHeight < 120;
        }
        return false;
    }, []);

    // Add a reliable scrollToBottom function
    const scrollToBottom = useCallback(() => {
        if (containerRef.current) {
            const scrollContainer = containerRef.current;
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
            setShowScrollToBottom(false);
        }
    }, []);

    // Fetch messages for the active room. This wasn't needed as it is done in Main Message Component
    useLayoutEffect(() => {
        handleGetRoomMessages();
        return () => {};
    }, [handleGetRoomMessages, scrollToBottom]);

    // Handle loading more messages
    const handleLoadMore = useCallback(() => {
        if (!messagesPagination.hasNextPage || loadingMore || !roomId || fetchingMessages || loadingOlderMessages) {
            return;
        }

        // Store current scroll position and height before loading more messages
        if (containerRef.current) {
            const container = containerRef.current;
            setScrollAnchorPosition(container.scrollTop);
            setScrollAnchorHeight(container.scrollHeight);
            setLoadingOlderMessages(true);
        }

        dispatch(
            getMessagesForSelectedRoom({
                roomID: roomId,
                userID: user_id,
                pagination: {
                    page: messagesPagination.currentPage + 1,
                    limit: messagesPagination.limit,
                },
            }),
        );
    }, [messagesPagination, loadingMore, roomId, user_id, dispatch, fetchingMessages, loadingOlderMessages]);

    // Restore scroll position after loading more messages
    useLayoutEffect(() => {
        if (
            loadingOlderMessages &&
            scrollAnchorPosition !== null &&
            scrollAnchorHeight !== null &&
            containerRef.current
        ) {
            requestAnimationFrame(() => {
                const container = containerRef.current;
                if (container) {
                    const newScrollHeight = container.scrollHeight;
                    const heightDifference = newScrollHeight - scrollAnchorHeight;
                    container.scrollTop = scrollAnchorPosition + heightDifference;

                    smoothScrollTo(container, scrollAnchorPosition + heightDifference);

                    // Reset scroll anchor state
                    setScrollAnchorPosition(null);
                    setScrollAnchorHeight(null);
                    setLoadingOlderMessages(false);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    // Scroll to bottom on initial load
    useLayoutEffect(() => {
        if (messagesPagination.currentPage === 1 && containerRef.current && !initialLoadCompleted) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
            setInitialLoadCompleted(true);
        }
    }, [messagesPagination, roomMessages, initialLoadCompleted]);

    // Auto-scroll only when user is already near bottom.
    useEffect(() => {
        if (loadingOlderMessages || updatingReaction.current || messages.length === 0) return;

        const latestMessageId = messages[0]?._id ?? null;
        const wasInitialized = prevLatestMessageIdRef.current !== null;
        const hasNewLatestMessage = wasInitialized && latestMessageId !== prevLatestMessageIdRef.current;

        if ((hasNewLatestMessage || !wasInitialized) && shouldStickToBottomRef.current && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
            setShowScrollToBottom(false);
        }

        prevLatestMessageIdRef.current = latestMessageId;
    }, [messages, loadingOlderMessages]);

    // Add scroll event listener to check if we need to show the scroll-to-bottom button
    useEffect(() => {
        const handleScroll = () => {
            shouldStickToBottomRef.current = isScrolledToBottom();
            if (containerRef.current && messages.length > 0) {
                // Show button based on number of messages scrolled past
                const messagesContainer = containerRef.current;
                const messageElements = messagesContainer.querySelectorAll('[id^="message-"]');

                if (messageElements.length === 0) {
                    setShowScrollToBottom(false);
                    return;
                }

                // Get the visible area boundaries
                const containerRect = messagesContainer.getBoundingClientRect();
                const containerBottom = containerRect.bottom;

                // Count how many messages are below the visible area
                let messagesScrolledPast = 0;

                // Start from the end (newest messages) and count backward
                for (let i = messageElements.length - 1; i >= 0; i--) {
                    const messageRect = messageElements[i].getBoundingClientRect();
                    if (messageRect.top > containerBottom) {
                        messagesScrolledPast++;
                    }
                }

                // Show button if scrolled past at least 10 messages
                setShowScrollToBottom(messagesScrolledPast >= 10);
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll();
            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
    }, [messages.length, isScrolledToBottom]);

    // Helper to get receiver info from room
    const getRecipientName = () => {
        // If rotating through multiple participants, show current participant's name
        if (otherParticipants.length > 2 && currentParticipant) {
            return createFullName(currentParticipant.user);
        }

        if (!room) return '';

        const isSender = room.sender && room.sender._id === user_id;

        if (isSender && room.receiver && room.receiver.length > 0) {
            // Current user is sender, show recipient(s)
            const recipients = room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id);

            if (recipients.length === 0) return 'No recipient';
            if (recipients.length === 1) {
                const recipient = recipients[0];
                if (typeof recipient === 'string') return 'User';
                return createFullName(recipient);
            }

            const firstRecipient = recipients[0];
            if (typeof firstRecipient === 'string') return `User +${recipients.length - 1}`;
            return `${createFullName(firstRecipient)} +${recipients.length - 1}`;
        } else {
            // Current user is receiver, show sender
            if (room.sender && typeof room.sender !== 'string') {
                const otherReceivers = room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id);

                if (otherReceivers.length > 0) {
                    return `${createFullName(room.sender)} +${otherReceivers.length}`;
                }
                return createFullName(room.sender);
            }
        }

        return 'Conversation';
    };

    // Helper to get recipient avatar - returns the user object(s) to render
    const getRecipientAvatars = () => {
        if (!room) return [];

        const isSender = room.sender && room.sender._id === user_id;

        if (isSender && room.receiver && room.receiver.length > 0) {
            // Current user is sender, show recipient avatars
            return room.receiver.filter(user => typeof user !== 'string' && user._id !== user_id).slice(0, 3); // Limit to first 3 users for avatar group
        } else {
            // Current user is receiver, show sender avatar
            if (room.sender && typeof room.sender !== 'string') {
                // Return sender as first, then add other receivers (excluding current user)
                const otherReceivers = room.receiver
                    .filter(user => typeof user !== 'string' && user._id !== user_id)
                    .slice(0, 2); // Limit to 2 additional users

                return [room.sender, ...otherReceivers];
            }
        }

        return [];
    };

    const recipientAvatarsForHeader = getRecipientAvatars();
    /** 2–3 avatars: smaller stack + typography so the title row stays in the same horizontal slot as a single avatar. */
    const headerMultipleRecipients = recipientAvatarsForHeader.length > 1;
    /** Same inner width as the 1-avatar column so the name block does not shift. */
    const headerAvatarStackLayoutWidthPx = 30;
    /** Horizontal offset between stacked avatars (small avatars must fit within `headerAvatarStackLayoutWidthPx`). */
    const headerAvatarOverlapStepPx = headerMultipleRecipients ? (recipientAvatarsForHeader.length >= 3 ? 5 : 10) : 16;
    const headerAvatarSize: 'large' | 'medium' | 'small' = headerMultipleRecipients
        ? 'small'
        : smallScreens
        ? 'medium'
        : 'large';

    /** Only show online / last seen after messages have loaded and the thread is not empty. */
    const showHeaderPresenceSubtitle =
        Boolean(isTypingText) || (!fetchingMessages && messages.length > 0 && Boolean(currentParticipant));

    // Get total participants count (excluding current user)
    const getTotalParticipantsCount = () => {
        if (!room) return 0;

        let count = 0;

        // Count receivers (excluding current user)
        if (room.receiver) {
            count += room.receiver.filter(user => {
                if (typeof user === 'string') return user !== user_id;
                return user._id !== user_id;
            }).length;
        }

        // Add sender if it's not the current user
        if (room.sender && typeof room.sender !== 'string' && room.sender._id !== user_id) {
            count += 1;
        }

        return count;
    };

    // Handle message input change
    const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;
        setMessage(text);

        if (text.length > 3 && room && !isCurrentlyTyping.current) {
            dispatch(typingStatusSocket({ status: 'yes', roomID: room._id }));
            isCurrentlyTyping.current = true;
        }

        if (typingTimeout.current) {
            clearTimeout(typingTimeout.current);
        }

        // debounce "no" typing event after user stops for 2 seconds
        typingTimeout.current = setTimeout(() => {
            if (room) {
                dispatch(typingStatusSocket({ status: 'no', roomID: room._id }));
                isCurrentlyTyping.current = false;
            }
        }, 2000);
    };

    // Handle attachments change
    const handleAttachmentsChange = (newAttachments: AttachmentType[]) => {
        // We only allow one attachment at a time
        setAttachments(newAttachments);
    };

    // Get user info from user ID
    const getUserInfo = (userId: string | IUserSummary): IUserSummary => {
        // If userId is already a user object, return it
        if (typeof userId !== 'string' && userId) {
            return userId;
        }

        // Find user in room participants or fallback to current user if it's the user's message
        if (room && room.receiver) {
            const foundUser = room.receiver.find(u => {
                if (typeof u === 'string') return u === userId;
                return u._id === userId;
            });

            if (foundUser && typeof foundUser !== 'string') {
                return foundUser;
            }
        }

        if (userId === user_id) {
            return currentUser;
        }

        // Return a default user object if user not found
        return user as unknown as IUserSummary;
    };

    // Modify formatAttachmentForSending to include file size validation and compression
    const formatAttachmentForSending = async (attachment: AttachmentType) => {
        let messageData: any = {
            type: attachment.type,
        };

        // Set message_type based on attachment type
        let message_type = 'chat';

        if (attachment.type === 'image') {
            const mediaAtt = attachment as IMediaAttachment;

            // Validate image sizes
            const oversizedImages = mediaAtt.files.filter(file => file.size > MAX_IMAGE_SIZE);
            if (oversizedImages.length > 0) {
                showToastMessage(`Images cannot exceed 3MB. Please select smaller images.`, 'error');
                throw new Error('Image size exceeds limit');
            }

            // Process images - compress if needed
            const processedFiles: File[] = [];
            const processedPreviews: string[] = [];

            // Process each file
            for (let i = 0; i < mediaAtt.files.length; i++) {
                const file = mediaAtt.files[i];

                // If image is above compression threshold, compress it
                if (file.size > COMPRESSION_THRESHOLD) {
                    try {
                        // Show compression notification
                        showToastMessage(`Compressing large image: ${file.name}`, 'info');

                        // Get compressed file
                        const compressedFile = await compressImage(file);
                        processedFiles.push(compressedFile);

                        // Generate new preview
                        const reader = new FileReader();
                        reader.readAsDataURL(compressedFile);
                        const preview = await new Promise<string>(resolve => {
                            reader.onloadend = () => {
                                resolve(reader.result as string);
                            };
                        });
                        processedPreviews.push(preview);

                        // Show success message
                        const compressionRatio = Math.round(100 - (compressedFile.size / file.size) * 100);
                        showToastMessage(`Reduced image size by ${compressionRatio}%`, 'success');
                    } catch (error) {
                        console.error('Compression failed', error);
                        // If compression fails, use original file
                        processedFiles.push(file);
                        processedPreviews.push(mediaAtt.previews[i]);
                    }
                } else {
                    // If file is small enough, use it as is
                    processedFiles.push(file);
                    processedPreviews.push(mediaAtt.previews[i]);
                }
            }

            // Update message data with processed files
            message_type = 'picture_message';
            messageData = {
                ...messageData,
                filenames: processedFiles.map(file => file.name),
                sizes: processedFiles.map(file => file.size),
                mimes: processedFiles.map(file => file.type),
                fileCount: processedFiles.length,
                previews: processedPreviews,
                files: processedFiles,
            };

            // Replace the original attachment files with processed ones
            mediaAtt.files = processedFiles;
            mediaAtt.previews = processedPreviews;
        } else if (attachment.type === 'document') {
            const mediaAtt = attachment as IMediaAttachment;

            // Validate document sizes
            const oversizedDocs = mediaAtt.files.filter(file => file.size > MAX_DOCUMENT_SIZE);
            if (oversizedDocs.length > 0) {
                showToastMessage(`Documents cannot exceed 1MB. Please select smaller files.`, 'error');
                throw new Error('Document size exceeds limit');
            }

            message_type = 'document_message';
            messageData = {
                ...messageData,
                // Handle multiple files
                filenames: mediaAtt.files.map(file => file.name),
                sizes: mediaAtt.files.map(file => file.size),
                mimes: mediaAtt.files.map(file => file.type),
                fileCount: mediaAtt.files.length,
            };
        } else if (attachment.type === 'video') {
            const projectAtt = attachment as IProjectAttachment;
            message_type = 'video_message';
            messageData = {
                ...messageData,
                // Send all project IDs
                projectIds: projectAtt.ids,
                mediaIds: projectAtt.ids, // Array of all video IDs
                titles: projectAtt.titles, // All titles
                thumbnailUrls: projectAtt.thumbnailUrls, // All thumbnails if available
            };
        } else if (attachment.type === 'audio') {
            const projectAtt = attachment as IProjectAttachment;
            message_type = 'sound_message';
            messageData = {
                ...messageData,
                // Send all audio IDs
                projectIds: projectAtt.ids,
                audioIds: projectAtt.ids, // Array of all audio IDs
                titles: projectAtt.titles, // All titles
            };
        } else if (attachment.type === 'location') {
            const locationAtt = attachment as ILocationAttachment;
            message_type = 'location_message';
            messageData = {
                ...messageData,
                location: {
                    type: 'Point',
                    coordinates: locationAtt.coord.map(Number),
                    address: locationAtt.addressDescription,
                    description: locationAtt.addressDescription,
                    imageUrl: locationAtt.imageUrl,
                },
            };
        }

        return {
            type: attachment.type,
            message_type,
            messageData,
        };
    };

    // Helper function to compress images
    const compressImage = (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            // Start with a quality of 0.7
            let quality = 0.7;

            // Adjust quality based on file size to target around 150KB
            if (file.size > 5 * 1024 * 1024) {
                // > 5MB
                quality = 0.5;
            } else if (file.size > 2 * 1024 * 1024) {
                // > 2MB
                quality = 0.6;
            }

            new Compressor(file, {
                quality: quality,
                maxWidth: 1600, // Reasonable max width for messaging
                maxHeight: 1600, // Reasonable max height for messaging
                convertSize: 150 * 1024, // Target size of 150KB
                success: compressedFile => {
                    // Convert Blob to File
                    const compressedImageFile = new File([compressedFile], file.name, { type: compressedFile.type });

                    // If still larger than target and not already at minimum quality
                    if (compressedImageFile.size > TARGET_COMPRESSED_SIZE && quality > 0.3) {
                        // Try again with lower quality
                        quality = Math.max(0.3, quality - 0.2);
                        new Compressor(file, {
                            quality: quality,
                            maxWidth: 1200,
                            maxHeight: 1200,
                            convertSize: 150 * 1024,
                            success: moreCompressedFile => {
                                const finalFile = new File([moreCompressedFile], file.name, {
                                    type: moreCompressedFile.type,
                                });
                                resolve(finalFile);
                            },
                            error: err => reject(err),
                        });
                    } else {
                        resolve(compressedImageFile);
                    }
                },
                error: err => reject(err),
            });
        });
    };

    // Modify handleSendMessage to scroll to bottom after sending
    const handleSendMessage = async () => {
        // Check if there's a message or attachments
        if ((!message.trim() && attachments.length === 0) || !roomId || !room) {
            console.log('Cannot send message - missing required data:', {
                message: message.trim(),
                attachments,
                roomId,
                room,
            });
            return;
        }

        // Send "not typing" when message is sent
        if (isCurrentlyTyping.current) {
            dispatch(typingStatusSocket({ status: 'no', roomID: room._id }));
            isCurrentlyTyping.current = false;
        }

        const now = new Date().toISOString();
        const uniqueSocketID = uuidv4();

        try {
            // Get the single attachment if it exists
            const attachment = attachments.length > 0 ? attachments[0] : null;

            // Determine the message type and attachment data
            let message_type = 'chat';
            let attachmentData = {};

            if (attachment) {
                // Process attachment (now async due to compression)
                const formattedAttachment = await formatAttachmentForSending(attachment);
                message_type = formattedAttachment.message_type;

                // Build attachment data based on type
                if (attachment.type === 'image') {
                    const mediaAtt = attachment as IMediaAttachment;
                    // For the image field, we'll set empty array that will be populated by backend after upload
                    attachmentData = {
                        image: mediaAtt.files.map(image => URL.createObjectURL(image)),
                        has_attachments: true,
                        // Include file metadata from the formatted attachment
                        filenames: mediaAtt.files.map(file => file.name),
                        sizes: mediaAtt.files.map(file => file.size),
                        mimes: mediaAtt.files.map(file => file.type),
                        fileCount: mediaAtt.files.length,
                        // Include temporary image previews for UI display
                        previews: mediaAtt.previews,
                        isLoading: true,
                        files: mediaAtt.files,
                    };
                } else if (attachment.type === 'document') {
                    const mediaAtt = attachment as IMediaAttachment;
                    attachmentData = {
                        document: mediaAtt.files.map(doc => URL.createObjectURL(doc)),
                        has_attachments: true,
                        // Include file metadata from the formatted attachment
                        filenames: mediaAtt.files.map(file => file.name),
                        sizes: mediaAtt.files.map(file => file.size),
                        mimes: mediaAtt.files.map(file => file.type),
                        fileCount: mediaAtt.files.length,
                        files: mediaAtt.files,
                    };
                } else if (attachment.type === 'video') {
                    const projectAtt = attachment as IProjectAttachment;
                    attachmentData = {
                        media: projectAtt.projects.map(proj => proj._id), // Array of ObjectIds
                        has_attachments: true,
                        // Include full projects for immediate display
                        tempProjects: projectAtt.projects,
                    };
                } else if (attachment.type === 'audio') {
                    const projectAtt = attachment as IProjectAttachment;
                    attachmentData = {
                        audio: projectAtt.projects.map(proj => proj._id), // Array of ObjectIds
                        has_attachments: true,
                        // Include full audios for immediate display
                        tempAudios: projectAtt.projects,
                    };
                } else if (attachment.type === 'location') {
                    attachmentData = {
                        location: formattedAttachment.messageData.location,
                        has_attachments: true,
                    };
                }
            }

            // Format the payload exactly as expected by the socket
            const messagePayload: any = {
                room: roomId,
                created_at: now,
                created_by: user_id,
                message_content: message.trim(),
                message_type: message_type as
                    | 'chat'
                    | 'video_message'
                    | 'picture_message'
                    | 'sound_message'
                    | 'location_message'
                    | 'document_message',
                is_reply: !!replyingTo,
                parent_message: replyingTo?._id,
                createdAt: now,
                uniqueSocketID,
                userObject: user as unknown as IUserSummary,
                ...(replyingTo && { replyingToObject: replyingTo }),
                // Include attachment data
                ...attachmentData,
            };

            dispatch(sendMessage(messagePayload));
            if (
                attachment &&
                (attachment.type === 'image' || attachment.type === 'document') &&
                attachmentData &&
                'files' in attachmentData &&
                Array.isArray(attachmentData.files)
            ) {
                dispatch(
                    queueFileUpload({
                        files: messagePayload.files,
                        socketId: uniqueSocketID,
                        path: attachment.type,
                        room: roomId,
                    }),
                );
            }
            setMessage('');
            setAttachments([]);
            setReplyingTo(null);

            // Scroll to bottom after sending the message with a short delay to ensure rendering
            setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.scrollTop = containerRef.current.scrollHeight;
                }
            }, 100);
        } catch (error) {
            console.error('Error sending message:', error);
            showToastMessage('Error sending message. Please try again.', 'error');
        }
    };

    // Send message on enter key press
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Handle back button click
    const handleBackClick = () => {
        setActiveRoomId(null);
        dispatch(toggleAvatarMessageModal(false));
        onClickMessageButton && onClickMessageButton();
    };

    // Emoji picker (mock implementation)
    const handleEmojiClick = (emoji: string) => {
        setMessage((prevMessage: any) => prevMessage + emoji);
    };

    // Format message timestamp
    const formatMessageTime = (timestamp: string | undefined) => {
        if (!timestamp) return '';
        return format(parseISO(timestamp), 'h:mm a');
    };

    // Format date for message separators
    const formatMessageDate = (timestamp: string) => {
        try {
            const date = parseISO(timestamp);
            if (isToday(date)) {
                return 'Today';
            } else if (isYesterday(date)) {
                return 'Yesterday';
            } else {
                return format(date, 'MMMM d, yyyy');
            }
        } catch (e) {
            return '';
        }
    };

    // Group messages by date
    const getMessageGroups = (messages: IMessage[]) => {
        const groups: { [key: string]: IMessage[] } = {};

        // Group messages by date
        messages.forEach(msg => {
            try {
                const date = parseISO(msg.createdAt!);
                const dateKey = format(date, 'yyyy-MM-dd');

                if (!groups[dateKey]) {
                    groups[dateKey] = [];
                }
                groups[dateKey].push(msg);
            } catch (e) {
                // If date parsing fails, put in a default group
                if (!groups['unknown']) {
                    groups['unknown'] = [];
                }
                groups['unknown'].push(msg);
            }
        });

        // Reverse messages within each group so most recent appears at bottom
        Object.values(groups).forEach(group => {
            group.reverse();
        });

        // Return date groups in chronological order
        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    };

    // Check if message is from current user
    const isCurrentUserMessage = (message: IMessage) => {
        // Handle both string and object formats for created_by
        if (typeof message.created_by === 'string') {
            return message.created_by === user_id;
        } else if (message.created_by && message.created_by._id) {
            return message.created_by._id === user_id;
        }
        return false;
    };

    // New functions for replies and reactions
    const handleReplyClick = (msg: IMessage) => {
        setReplyingTo(msg);
        // Focus on message input
        const inputElement = document.querySelector('input[placeholder="Type a message..."]') as HTMLInputElement;
        if (inputElement) {
            inputElement.focus();
        }
    };

    const cancelReply = () => {
        setReplyingTo(null);
    };

    const scrollToMessage = (messageId: string) => {
        const messageElement = document.getElementById(`message-${messageId}`);
        if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight the message briefly
            messageElement.style.backgroundColor = theme[themeStyle].highlight + '40';
            setTimeout(() => {
                messageElement.style.backgroundColor = '';
            }, 1500);
        }
    };

    const handleMessageLongPress = (e: React.MouseEvent<HTMLDivElement>, messageId: string) => {
        e.preventDefault();
        // The reaction menu is now handled within the MessageBubble component
    };

    // Convert standard IMessageReaction[] to our custom format for UI
    const convertReactionsToCustomFormat = (reactions: IMessageReaction[] = []): ICustomReaction[] => {
        const emojiMap = new Map<string, string[]>();

        reactions.forEach(reaction => {
            if (!emojiMap.has(reaction.reaction)) {
                emojiMap.set(reaction.reaction, []);
            }
            emojiMap.get(reaction.reaction)?.push(reaction.user);
        });

        return Array.from(emojiMap.entries()).map(([emoji, users]) => ({
            emoji,
            users,
        }));
    };

    // Handle reaction click
    const handleReactionClick = (emoji: string, messageId?: string) => {
        if (!messageId || !containerRef.current) return;

        // CRITICAL: Save scroll position BEFORE any state changes
        const container = containerRef.current;
        const savedScrollTop = container.scrollTop;

        // Flag to prevent auto-scrolling
        updatingReaction.current = true;

        console.log('REACTION - Saving position:', savedScrollTop);

        // First dispatch the action to update backend
        const hasReacted = messages
            .find(m => m._id === messageId)
            ?.reactions?.some(r => r.reaction === emoji && r.user === user_id);
        if (!roomId) return;
        dispatch(
            reactToMessage({
                messageId: messageId,
                reaction: emoji,
                room: roomId,
                remove: hasReacted,
            }),
        );

        // Then update local state
        setMessages(prevMessages =>
            prevMessages.map(msg => {
                if (msg._id === messageId) {
                    if (hasReacted) {
                        return {
                            ...msg,
                            reactions: (msg.reactions || []).filter(r => !(r.reaction === emoji && r.user === user_id)),
                        };
                    } else {
                        const filteredReactions = (msg.reactions || []).filter(r => r.user !== user_id);
                        return {
                            ...msg,
                            reactions: [...filteredReactions, { user: user_id, reaction: emoji }],
                        };
                    }
                }
                return msg;
            }),
        );

        // Create a function to restore scroll position
        // const restoreScrollPosition = () => {
        //     if (container) {
        //         console.log('REACTION - Restoring position:', savedScrollTop);
        //         container.scrollTop = savedScrollTop;
        //     }
        // };

        // Apply multiple strategies to ensure scroll position is restored

        // 1. Immediate scheduling
        // window.requestAnimationFrame(() => {
        //     restoreScrollPosition();

        //     // 2. Delayed restoration as backup
        //     setTimeout(() => {
        //         restoreScrollPosition();

        //         // 3. One final check after everything has settled
        //         setTimeout(() => {
        //             restoreScrollPosition();
        //             updatingReaction.current = false;
        //         }, 100);
        //     }, 50);
        // });
    };

    // Format user names for reaction tooltip
    const formatReactionUsers = (reaction: ICustomReaction) => {
        // In a real app, we would have user data to display names
        return `${reaction.users.length} ${reaction.users.length === 1 ? 'person' : 'people'}`;
    };

    // Add the handlers for editing and deleting messages
    const handleDeleteMessage = (messageId: string) => {
        if (!roomId) return;
        // Instead of removing the message, mark it as deleted
        dispatch(
            deleteMessage({
                messageId: messageId,
                room: roomId,
            }),
        );
        setMessages(prevMessages =>
            prevMessages.map(msg => {
                if (msg._id === messageId) {
                    return {
                        ...msg,
                        deleted: true,
                    };
                }
                return msg;
            }),
        );

        // Here you would also dispatch an action to mark the message as deleted on the backend
        // For example:
        // dispatch(markMessageAsDeleted({ messageId, roomId }));
    };

    const handleEditMessage = (messageId: string, newContent: string) => {
        // Update the message in the local state
        if (roomId) {
            dispatch(
                updateMessage({
                    message_content: newContent,
                    messageId: messageId,
                    room: roomId,
                }),
            );
            setMessages(prevMessages =>
                prevMessages.map(msg => {
                    if (msg._id === messageId) {
                        return {
                            ...msg,
                            message_content: newContent,
                            edited: true,
                        };
                    }
                    return msg;
                }),
            );

            // Here you would also dispatch an action to update the message in the backend
            // For example:
            // dispatch(updateMessage({ messageId, content: newContent, roomId }));
        }
    };

    const renderMessageGroup = useMemo(() => {
        // Memoize the MessageBubble component
        const MemoizedMessageBubble = React.memo(MessageBubble, (prevProps, nextProps) => {
            // Deep comparison for message content
            const messageChanged =
                prevProps.message._id !== nextProps.message._id ||
                prevProps.message.message_content !== nextProps.message.message_content ||
                prevProps.message.deleted !== nextProps.message.deleted ||
                prevProps.message.edited !== nextProps.message.edited ||
                prevProps.message.image !== nextProps.message.image ||
                prevProps.message.document !== nextProps.message.document ||
                prevProps.message.media !== nextProps.message.media ||
                prevProps.message.audio !== nextProps.message.audio ||
                prevProps.message.location !== nextProps.message.location ||
                JSON.stringify(prevProps.message.reactions) !== JSON.stringify(nextProps.message.reactions);

            // Only re-render if message content or user context changed
            return (
                !messageChanged &&
                prevProps.isCurrentUser === nextProps.isCurrentUser &&
                prevProps.isPrevSameUser === nextProps.isPrevSameUser &&
                prevProps.isNextSameUser === nextProps.isNextSameUser &&
                prevProps.user_id === nextProps.user_id
            );
        });

        return getMessageGroups(messages).map(([dateKey, msgs]) => (
            <React.Fragment key={dateKey}>
                {/* Date separator */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        my: 2.5,
                    }}>
                    <Typography
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            border: `1px solid ${theme[themeStyle].darkGray60}`,
                            bgcolor: theme[themeStyle].deep_grey,
                            color: theme[themeStyle].true_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 11,
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                        }}>
                        {formatMessageDate(msgs[0].createdAt)}
                    </Typography>
                </Box>

                {/* Messages for this date */}
                {msgs.map((msg, index) => {
                    const isCurrentUser = isCurrentUserMessage(msg);
                    const prevMsg = index > 0 ? msgs[index - 1] : null;
                    const nextMsg = index < msgs.length - 1 ? msgs[index + 1] : null;
                    const isPrevSameUser = prevMsg && isCurrentUserMessage(prevMsg) === isCurrentUser ? true : false;
                    const isNextSameUser = nextMsg && isCurrentUserMessage(nextMsg) === isCurrentUser ? true : false;

                    return (
                        <Box key={msg._id} id={`message-${msg._id}`} sx={{ width: '100%' }}>
                            <MemoizedMessageBubble
                                message={msg}
                                isCurrentUser={isCurrentUser}
                                onReply={handleReplyClick}
                                onReactionClick={emoji => handleReactionClick(emoji, msg._id)}
                                formatMessageTime={formatMessageTime}
                                getUserInfo={getUserInfo}
                                formatReactionUsers={formatReactionUsers}
                                convertReactionsToCustomFormat={convertReactionsToCustomFormat}
                                scrollToMessage={scrollToMessage}
                                handleMessageLongPress={handleMessageLongPress}
                                user_id={user_id}
                                isPrevSameUser={isPrevSameUser}
                                isNextSameUser={isNextSameUser}
                                replyingTo={msg.parent_message}
                                onDeleteMessage={handleDeleteMessage}
                                onEditMessage={handleEditMessage}
                            />
                        </Box>
                    );
                })}
            </React.Fragment>
        ));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: theme[themeStyle].secondaryBg,
                zIndex: 10,
                borderRadius: embedFullScreen ? 0 : '16px 16px 0 0',
                height: '100%',
                px: 2.5,
                backdropFilter: 'blur(8px)',
            }}>
            {/* Conversation Header */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '64px',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: theme[themeStyle].deep_grey,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    zIndex: 10,
                }}>
                {!isExpanded && (
                    <IconButton
                        edge="start"
                        onClick={handleBackClick}
                        sx={{
                            color: theme[themeStyle].tag_white,
                            display: { xs: 'flex', md: 'flex' }, // show on modal & mobile
                            flexShrink: 0,
                        }}>
                        {!isExpanded && <ArrowBack />}
                    </IconButton>
                )}
                {/* Avatar group or single avatar */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flex: 1,
                        minWidth: 0,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        left: 0,
                        right: 0,
                    }}>
                    <Box
                        sx={{
                            mr: 2,
                            position: 'relative',
                            flexShrink: 0,
                            width: `${headerAvatarStackLayoutWidthPx}px`,
                            height: 40,
                            overflow: 'visible',
                        }}>
                        <Box
                            sx={{
                                width: `${headerAvatarStackLayoutWidthPx}px`,
                                height: '100%',
                                position: 'relative',
                            }}>
                            {recipientAvatarsForHeader.slice(0, 3).map((user, index) => (
                                <Box
                                    key={user._id || index}
                                    sx={{
                                        position: 'absolute',
                                        top: headerMultipleRecipients
                                            ? { xs: '10px', smMd: '6px' }
                                            : { xs: '5px', smMd: '2px' },
                                        left: index * headerAvatarOverlapStepPx,
                                        zIndex: 3 - index,
                                        border: `1px solid ${theme[themeStyle].secondaryBg}`,
                                        borderRadius: '50%',
                                    }}>
                                    <ThspianAvatar user={user} size={headerAvatarSize} withMonogram />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                        }}>
                        {/* Show count indicator if there are more participants than shown */}
                        {getTotalParticipantsCount() > 3 && (
                            <Box
                                sx={{
                                    right: 0,
                                    bottom: 0,
                                    color: theme[themeStyle].true_white,
                                    borderRadius: '50%',
                                    width: 22,
                                    height: 22,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 11,
                                    fontWeight: 'bold',
                                    border: `1px solid ${theme[themeStyle].secondaryBg}`,
                                    zIndex: 4,
                                }}>
                                +{getTotalParticipantsCount() - Math.min(3, recipientAvatarsForHeader.length)}
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                minWidth: 0,
                                width: '100%',
                            }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        display: 'block',
                                        justifyContent: 'flex-start',
                                        fontFamily: FONT_ENUM.BOLD_TEXT,
                                        color: theme[themeStyle].tag_white,
                                        textTransform: 'capitalize',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: headerMultipleRecipients
                                            ? { xs: '11px', sm: '12px' }
                                            : { xs: '12px', sm: '14px' },
                                        maxWidth: '100%',
                                        textAlign: 'left',
                                    }}>
                                    {getRecipientName()}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    minHeight: showHeaderPresenceSubtitle ? (headerMultipleRecipients ? 12 : 14) : 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    minWidth: 0,
                                    width: '100%',
                                }}>
                                {isTypingText ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            minWidth: 0,
                                            width: '100%',
                                        }}>
                                        <Typography
                                            sx={{
                                                display: 'block',
                                                color: theme[themeStyle].highlight,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: headerMultipleRecipients ? 10 : 11,
                                                lineHeight: 1.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                minWidth: 0,
                                                width: '100%',
                                                textAlign: 'left',
                                            }}>
                                            {isTypingText}
                                        </Typography>
                                    </Box>
                                ) : !fetchingMessages && messages.length > 0 && currentParticipant ? (
                                    <Box
                                        key={`${currentParticipant.user._id}-${currentParticipantIndex}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            minWidth: 0,
                                            animation:
                                                otherParticipants.length > 2 ? 'fadeInRotate 0.5s ease-in-out' : 'none',
                                            '@keyframes fadeInRotate': {
                                                '0%': {
                                                    opacity: 0,
                                                    transform: 'rotateY(-90deg)',
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                    transform: 'rotateY(0deg)',
                                                },
                                            },
                                        }}>
                                        {currentParticipant.status === 'online' ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    lineHeight: 0,
                                                    gap: 0.5,
                                                }}>
                                                {/* Recreated StyledBadge dot (exact match) */}
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#44b700',
                                                        flexShrink: 0,

                                                        // ripple effect (same as StyledBadge)
                                                        '&::after': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            borderRadius: '50%',
                                                            border: '1px solid currentColor',
                                                            animation: 'ripple 1.2s infinite ease-in-out',
                                                        },
                                                    }}
                                                />

                                                {/* Ripple animation (must exist once in DOM scope) */}
                                                <Box
                                                    component="style"
                                                    dangerouslySetInnerHTML={{
                                                        __html: `
                @keyframes ripple {
                    0% {
                        transform: scale(.8);
                        opacity: 1;
                    }
                    100% {
                        transform: scale(2.4);
                        opacity: 0;
                    }
                }
                `,
                                                    }}
                                                />

                                                <Typography
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        color: theme[themeStyle].tag_white,
                                                        fontSize: headerMultipleRecipients ? '7px' : '8px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        minWidth: 0,
                                                        lineHeight: 1,
                                                    }}>
                                                    online
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography
                                                sx={{
                                                    display: 'block',
                                                    color: theme[themeStyle].tag_white,
                                                    fontSize: headerMultipleRecipients ? '7px' : '8px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    minWidth: 0,
                                                    width: '100%',
                                                }}>
                                                {currentParticipant.lastSeen || ''}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {!isExpanded && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            justifyContent: 'flex-end',
                            flexShrink: 0,
                        }}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (!sessionStorage.getItem('preMessagesRoute')) {
                                    sessionStorage.setItem('preMessagesRoute', window.location.pathname);
                                }
                                dispatch(setMessagesExpandedReturnTarget('avatar-panel'));
                                dispatch(toggleIsMessageModalExpanded(true));
                                onRoomClick && onRoomClick(roomId);
                            }}>
                            <ExpandIcon height={24} width={24} stroke={theme[themeStyle].primary_white} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => {
                                dispatch(toggleAvatarMessageModal(false));
                            }}>
                            <CloseIcon sx={{ color: theme[themeStyle].primary_white }} />
                        </IconButton>
                    </Box>
                )}
            </Box>

            {/* Messages Area - Scrollable */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '64px',
                    left: 0,
                    right: 0,
                    bottom: attachments.length > 0 ? '148px' : replyingTo ? '148px' : '80px',
                    overflowY: 'auto',
                    p: 1,
                    bgcolor: theme[themeStyle].deep_grey,
                }}>
                {fetchingMessages ? (
                    <MessageSkeletonGroup />
                ) : messages.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            No messages yet
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                textAlign: 'center',
                                mt: 1,
                            }}>
                            Start the conversation by typing a message below
                        </Typography>
                    </Box>
                ) : (
                    <ScrollableContainer
                        onBeginningReached={handleLoadMore}
                        threshold={50}
                        ref={containerRef}
                        loading={loadingMore}>
                        {renderMessageGroup}
                        <div ref={messagesEndRef} />
                    </ScrollableContainer>
                )}
            </Box>

            {/* Scroll to bottom button - appears when scrolled up */}
            {showScrollToBottom && (
                <Fab
                    size="small"
                    aria-label="scroll to bottom"
                    onClick={scrollToBottom}
                    sx={{
                        position: 'absolute',
                        bottom: attachments.length > 0 ? '158px' : replyingTo ? '158px' : '90px',
                        left: '16px',
                        zIndex: 11,
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].true_white,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].highlight + 'CC',
                        },
                        boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                    }}>
                    <KeyboardArrowDown />
                </Fab>
            )}

            {/* Message Input Area - with fixed position */}
            <MessageInput
                message={message}
                onMessageChange={handleMessageChange}
                onSendMessage={handleSendMessage}
                onKeyPress={handleKeyPress}
                room={room}
                onEmojiClick={handleEmojiClick}
                attachments={attachments}
                onAttachmentsChange={handleAttachmentsChange}
                replyingTo={replyingTo}
                onCancelReply={cancelReply}
                getUserInfo={getUserInfo}
            />
        </Box>
    );
};

export { FloatingConversationView };
